import type { GridPaginationModel, GridSortModel } from '@mui/x-data-grid';
import { useEffect, useMemo, useState } from 'react';
import { useIdentity, usePayment } from '$api-clients/hooks';
import { usePerformanceReportContext } from '../../PerformanceReport/context/PerformanceReportContext';
import { useAllTimeStartDate } from '../../ReportsConfiguration/useReportsConfiguration';
import {
  buildTransactionsSummaryQuery,
  DEFAULT_PAGE_SIZE,
  mapTransactionToRow,
  type TransactionsTableResult,
  type TransactionsTableRow,
} from '../TransactionsTable.helpers';

const EMPTY_RESULTS: TransactionsTableResult[] = [];

interface UseGetTransactionsProps {
  sortModel: GridSortModel;
  viewReportAs: string;
}

interface UseGetTransactionsResult {
  rows: TransactionsTableRow[];
  loading: boolean;
  totalCount: number | undefined;
  rowCount: number | undefined;
  paginationModel: GridPaginationModel;
  setPaginationModel: React.Dispatch<React.SetStateAction<GridPaginationModel>>;
}

export function useGetTransactions({
  sortModel,
  viewReportAs,
}: UseGetTransactionsProps): UseGetTransactionsResult {
  const { filters } = usePerformanceReportContext();
  const allTimeStartDate = useAllTimeStartDate();
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: DEFAULT_PAGE_SIZE,
  });

  // Reset to page 0 when filters change. Returns the same object reference when
  // already on page 0 so React bails out without a re-render, preventing a double fetch.
  useEffect(() => {
    setPaginationModel((prev) =>
      prev.page === 0 ? prev : { ...prev, page: 0 },
    );
  }, [filters]);

  const query = useMemo(
    () =>
      buildTransactionsSummaryQuery(
        filters,
        sortModel,
        viewReportAs,
        {
          offset: paginationModel.page * paginationModel.pageSize,
          limit: paginationModel.pageSize,
        },
        allTimeStartDate,
      ),
    [filters, sortModel, viewReportAs, paginationModel, allTimeStartDate],
  );

  const { data: transactionsData, isLoading: isLoadingTransactions } =
    usePayment('/reporting/summary', { params: { query } });

  const results = transactionsData?.results ?? EMPTY_RESULTS;

  // Collect all unique agent IDs across all rows' agentList arrays.
  const userIds = useMemo(
    () => [...new Set(results.flatMap((r) => r.agentList ?? []))].sort(),
    [results],
  );
  const teamIds = useMemo(
    () =>
      [
        ...new Set(
          results.flatMap((r) => (r.teamIdentityId ? [r.teamIdentityId] : [])),
        ),
      ].sort(),
    [results],
  );

  const { data: usersData, isLoading: isLoadingUsers } = useIdentity(
    '/users',
    userIds.length > 0
      ? { params: { query: { ids: userIds.join(',') } } }
      : null,
  );
  const { data: teamsData, isLoading: isLoadingTeams } = useIdentity(
    '/teams',
    teamIds.length > 0
      ? { params: { query: { ids: teamIds.join(',') } } }
      : null,
  );

  const usersById = useMemo(
    () => Object.fromEntries((usersData?.data ?? []).map((u) => [u.id, u])),
    [usersData],
  );
  const teamsById = useMemo(
    () => Object.fromEntries((teamsData?.data ?? []).map((t) => [t.id, t])),
    [teamsData],
  );

  const rows = useMemo(
    () => results.map((r) => mapTransactionToRow(r, { usersById, teamsById })),
    [results, usersById, teamsById],
  );

  const isLoading = isLoadingTransactions || isLoadingUsers || isLoadingTeams;

  return {
    rows,
    loading: isLoading,
    totalCount: transactionsData?.totalCount,
    rowCount: results.length,
    paginationModel,
    setPaginationModel,
  };
}
